import {v4 as uuidv4} from "uuid";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_ERROR} from "../../hocs/snackbar-provider";
import {REQUEST_ERROR, REQUEST_INIT, REQUEST_SUCCESS} from "../request/requestActions";
import {CRITERIA_SELECTION_MODE_ALL, CRITERIA_SELECTION_TYPE_PARTIAL} from "../../state/dataset/constants";
import {
  DATASET_SV_DATASET_FETCH,
  fetchDatasetSVDatasetError,
  fetchDatasetSVDatasetInit,
  fetchDatasetSVDatasetSuccess
} from "../../state/dataset/single-viewer/actions";
import {addSpinnerMessage, markSpinnerMessage} from "../../state/spinner/spinnerActions";
import {
  getFilteredChartLayout,
  getFilteredMapLayout,
  getFilteredTableLayout,
  getInitialChartLayout,
  getInitialMapLayout,
  getInitialTableLayout,
  getPointDataInitialMapLayout
} from "../../utils/dataset";

const singleViewerMiddlewareFactory =
  t =>
  ({dispatch, getState}) =>
  next =>
  action => {
    const result = next(action);

    if (action?.payload?.label === DATASET_SV_DATASET_FETCH) {
      if (action.type === REQUEST_INIT) {
        dispatch(fetchDatasetSVDatasetInit());
      } else if (action.type === REQUEST_SUCCESS) {
        const {mapConfig = {}, tableConfig = {}} = getState()?.appConfig || {};
        const {initAsFlatTable = false} = getState()?.themeConfig ?? {};
        const configState = getState()?.config || {};
        const datasetState = getState()?.dataset?.commons || {};
        const datasetSVState = getState()?.dataset?.singleViewer || {};

        const spinnerUuid = uuidv4();
        dispatch(
          addSpinnerMessage(
            spinnerUuid,
            t("middlewares.fetchDatasetAsyncHandlerMiddlewareFactory.spinners.layoutHandling")
          )
        );

        const {response, extra} = action.payload;

        const isEmptyData = extra.status === 204 || (response?.id || "").length === 0;
        const isPartialData = extra.status === 206;

        const isResponseNotValid = !response || isEmptyData;

        let tableLayout = null;
        let mapLayout = null;
        let chartLayoutCartesian = null;
        let chartLayoutRadial = null;

        const territoryDim = datasetSVState.territoryDim;
        const timeDim = datasetSVState.timeDim;
        const measureDim = datasetSVState.measureDim;

        let isPointData = datasetSVState.isPointData;
        let pointDim = datasetSVState.pointDim;

        if (isPointData) {
          const datasetId = response?.extension?.datasets?.[0] || null;

          const latitudeId = datasetSVState.latAttributeId;
          const longitudeId = datasetSVState.longAttributeId;
          const latAttribute =
            (response?.extension?.attributes?.[datasetId]?.series || []).find(({id}) => id === latitudeId) || null;
          const longAttribute =
            (response?.extension?.attributes?.[datasetId]?.series || []).find(({id}) => id === longitudeId) || null;
          const latAttachedDims = latAttribute?.relationship?.dimensions || [];
          const longAttachedDims = longAttribute?.relationship?.dimensions || [];
          if (
            latAttachedDims.length === 1 &&
            longAttachedDims.length === 1 &&
            latAttachedDims[0] === longAttachedDims[0]
          ) {
            pointDim = latAttachedDims[0];
          } else {
            isPointData = false;
            pointDim = null;
          }
        } else {
          pointDim = null;
        }

        if (!isResponseNotValid) {
          if (datasetSVState.tableLayout) {
            tableLayout = getFilteredTableLayout(datasetSVState.tableLayout, response, false);
          } else {
            tableLayout = getInitialTableLayout(
              response,
              territoryDim,
              timeDim,
              measureDim,
              initAsFlatTable,
              tableConfig.maxFlatTableColCount
            );
          }
        }

        if (
          !isResponseNotValid &&
          ((territoryDim && response.id.includes(territoryDim)) || (pointDim && response.id.includes(pointDim)))
          //! TODO: ripristinare controllo su singola geometria?
        ) {
          const pointCount =
            isPointData && pointDim && response.id.includes(pointDim)
              ? response.size[response.id.indexOf(pointDim)]
              : null;
          if (pointCount !== null && pointCount > mapConfig.maxPointCount) {
            mapLayout = null;
          } else {
            if (datasetSVState.mapLayout) {
              mapLayout = getFilteredMapLayout(datasetSVState.mapLayout, response, isPointData);
            } else {
              mapLayout = isPointData
                ? getPointDataInitialMapLayout(response, pointDim, timeDim, measureDim)
                : getInitialMapLayout(response, territoryDim);
            }
          }
        }

        if (!isResponseNotValid) {
          if (datasetSVState.chartLayoutCartesian) {
            chartLayoutCartesian = getFilteredChartLayout(datasetSVState.chartLayoutCartesian, response);
          } else {
            chartLayoutCartesian = getInitialChartLayout(response, territoryDim);
          }

          if (datasetSVState.chartLayoutRadial) {
            chartLayoutRadial = getFilteredChartLayout(datasetSVState.chartLayoutRadial, response);
          } else {
            chartLayoutRadial = getInitialChartLayout(response, territoryDim);
          }
        }

        const myWorker = new Worker(
          window.location.origin + configState.clientBasePath + "/workers/fetchDatasetSVAsyncHandlerWorker.js"
        );
        myWorker.onmessage = event => {
          const {
            tableLayout,
            tableFilterTree,
            mapLayout,
            mapFilterTree,
            chartLayoutCartesian,
            chartLayoutRadial,
            chartFilterTreeCartesian,
            chartFilterTreeRadial,
            timePeriodsByFreq
          } = event.data;
          dispatch(
            fetchDatasetSVDatasetSuccess(
              response,
              extra.status,
              extra.responseHeaders,
              isEmptyData,
              isPartialData,
              isResponseNotValid,
              tableLayout,
              tableFilterTree,
              mapLayout,
              mapFilterTree,
              chartLayoutCartesian,
              chartLayoutRadial,
              chartFilterTreeCartesian,
              chartFilterTreeRadial,
              timePeriodsByFreq,
              isPointData,
              pointDim,
              myWorker
            )
          );
          dispatch(markSpinnerMessage(spinnerUuid));
        };
        myWorker.onerror = () => {
          dispatch(fetchDatasetSVDatasetError());
          dispatch(markSpinnerMessage(spinnerUuid, true));
          showMessage(
            t("middlewares.fetchDatasetAsyncHandlerMiddlewareFactory.feedback.layoutHandlingError"),
            SNACKBAR_MESSAGE_SEVERITY_ERROR
          );
        };
        myWorker.postMessage({
          response,
          tableLayout: tableLayout,
          mapLayout: mapLayout,
          chartLayoutCartesian: chartLayoutCartesian,
          chartLayoutRadial: chartLayoutRadial,
          tableDefaultFiltersValue:
            datasetState.mode === CRITERIA_SELECTION_MODE_ALL && datasetState.type === CRITERIA_SELECTION_TYPE_PARTIAL
              ? datasetSVState.tableDefaultFiltersValue
              : null,
          chartDefaultFiltersValue:
            datasetState.mode === CRITERIA_SELECTION_MODE_ALL && datasetState.type === CRITERIA_SELECTION_TYPE_PARTIAL
              ? datasetSVState.chartDefaultFiltersValue
              : null,
          criteria: datasetSVState.criteria,
          isFirstFetch: datasetSVState.isFirstFetch,
          isMultiViewer: false
        });
      } else if (action.type === REQUEST_ERROR) {
        dispatch(fetchDatasetSVDatasetError(action.payload.statusCode));
      }
    }

    return result;
  };

export default singleViewerMiddlewareFactory;
